// State Preservation Service for OMNIGATE
export interface TabState {
  id: string;
  activeTab: string;
  chatMessages?: any[];
  logFilters?: {
    level: string;
    searchTerm: string;
    autoScroll: boolean;
  };
  visionObserver?: {
    isActive: boolean;
    settings: any;
  };
  uiState?: {
    sidebarCollapsed: boolean;
    panelSizes: Record<string, number>;
  };
  timestamp: string;
}

export class StatePreservation {
  private static readonly STATE_KEY = 'omnigate_tab_states';
  private static readonly ACTIVE_TAB_KEY = 'omnigate_active_tab';
  private static readonly MAX_STATES = 10;

  // Save current tab state
  public static saveTabState(tabId: string, state: Omit<TabState, 'id' | 'timestamp'>): TabState {
    const tabState: TabState = {
      id: tabId,
      ...state,
      timestamp: new Date().toISOString()
    };

    const allStates = this.getAllStates();
    
    // Remove existing state for this tab
    const filtered = allStates.filter(s => s.id !== tabId);
    
    // Add new state at beginning
    filtered.unshift(tabState);
    
    // Keep only recent states
    const trimmed = filtered.slice(0, this.MAX_STATES);
    
    localStorage.setItem(this.STATE_KEY, JSON.stringify(trimmed));
    localStorage.setItem(this.ACTIVE_TAB_KEY, tabId);
    
    return tabState;
  }

  // Get state for specific tab
  public static getTabState(tabId: string): TabState | null {
    const states = this.getAllStates();
    return states.find(s => s.id === tabId) || null;
  }

  // Get all saved states
  public static getAllStates(): TabState[] {
    try {
      const stored = localStorage.getItem(this.STATE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to parse tab states from localStorage:', e);
      return [];
    }
  }

  // Get currently active tab
  public static getActiveTab(): string | null {
    return localStorage.getItem(this.ACTIVE_TAB_KEY);
  }

  // Restore state for a tab
  public static restoreTabState(tabId: string): TabState | null {
    const state = this.getTabState(tabId);
    if (state) {
      localStorage.setItem(this.ACTIVE_TAB_KEY, tabId);
    }
    return state;
  }

  // Clear state for specific tab
  public static clearTabState(tabId: string): void {
    const states = this.getAllStates();
    const filtered = states.filter(s => s.id !== tabId);
    localStorage.setItem(this.STATE_KEY, JSON.stringify(filtered));
  }

  // Clear all states
  public static clearAllStates(): void {
    localStorage.removeItem(this.STATE_KEY);
    localStorage.removeItem(this.ACTIVE_TAB_KEY);
  }

  // Export states
  public static exportStates(): string {
    const data = {
      states: this.getAllStates(),
      activeTab: this.getActiveTab()
    };
    return JSON.stringify(data, null, 2);
  }

  // Import states
  public static importStates(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.states && Array.isArray(data.states)) {
        localStorage.setItem(this.STATE_KEY, JSON.stringify(data.states));
      }
      
      if (data.activeTab) {
        localStorage.setItem(this.ACTIVE_TAB_KEY, data.activeTab);
      }
      
      return true;
    } catch (e) {
      console.error('Failed to import states:', e);
      return false;
    }
  }

  // Get state statistics
  public static getStatistics(): {
    totalTabs: number;
    activeTab: string | null;
    lastUpdated: string | null;
    storageSize: number;
  } {
    const states = this.getAllStates();
    const activeTab = this.getActiveTab();
    const latestState = states[0] || null;
    
    return {
      totalTabs: states.length,
      activeTab,
      lastUpdated: latestState ? latestState.timestamp : null,
      storageSize: new Blob([JSON.stringify(states)]).size
    };
  }

  // Cleanup old states (older than 7 days)
  public static cleanupOldStates(days: number = 7): number {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const cutoffISOString = cutoffDate.toISOString();
    const states = this.getAllStates();
    const filtered = states.filter(state => state.timestamp > cutoffISOString);
    
    const removedCount = states.length - filtered.length;
    
    if (removedCount > 0) {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(filtered));
    }
    
    return removedCount;
  }
}

// React hook for state management
export const useTabState = (tabId: string) => {
  const saveState = (state: Omit<TabState, 'id' | 'timestamp'>) => {
    return StatePreservation.saveTabState(tabId, state);
  };

  const getState = () => {
    return StatePreservation.getTabState(tabId);
  };

  const restoreState = () => {
    return StatePreservation.restoreTabState(tabId);
  };

  return {
    saveState,
    getState,
    restoreState,
    getActiveTab: StatePreservation.getActiveTab,
    getAllStates: StatePreservation.getAllStates
  };
};